import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  DisburseFromLineOfCreditSuccess,
  PostDisbursementAction,
  Typenames,
  VlocReviewPageInput,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import LineOfCreditErrorScreenComponent, {
  VlocStepName,
} from 'features/lineOfCredit/screens/error/LineOfCreditErrorScreenComponent';
import { storePaymentSchedule } from 'features/lineOfCredit/screens/paymentSchedule/paymentScheduleRouteStore';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import ExpressiveLgText from 'shared/components/vxl/typography/expressive/ExpressiveLgText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import HyperlinkSmText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkSmText';
import HyperlinkXsText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkXSText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

import useGetVlocReviewPage from './hooks/useGetVlocReviewPage';
import useSubmitVlocApplication from './hooks/useSubmitVlocApplication';

const ReviewScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('vloc');

  const { bottom } = useSafeAreaInsets();

  const bottomSheetRef = useRef<BottomSheetModal>(null);

  const dismissToTab = useDismissToRootTab();

  const goHome = () => {
    dismissToTab(Tabs.HOME);
  };

  const { tierId, subcategory = '' } = useLocalSearchParams<{
    tierId: string;
    subcategory: string;
    name: string;
  }>();

  const input: VlocReviewPageInput = {
    tierId: tierId,
  };

  useViewDidFocusScreenAnalyticsEvent({
    key: '/varo-line-of-credit/review-custom',
    customAttributes: {
      subcategory,
    },
  });

  const { result, fetching, error } = useGetVlocReviewPage({ input });

  const {
    response: reviewData,
    triggerMutation,
    fetching: isFetching,
    error: mutationError,
  } = useSubmitVlocApplication();

  const submitReviewDetails = (tierId: string, autoPayEnabled: boolean) => {
    triggerMutation(tierId, autoPayEnabled);
  };

  const goBack = () => {
    router.back();
  };

  const reviewInfo =
    result?.__typename === Typenames.VlocReviewPageView ? result : null;

  const [autoPay, setAutoPay] = useState(false);

  useEffect(() => {
    if (reviewInfo?.isAutoPayEnabled !== undefined) {
      setAutoPay(reviewInfo.isAutoPayEnabled);
    }
  }, [reviewInfo]);

  const [showError, setShowError] = useState(false);

  const [content, setContent] = useState({ title: '', description: '' });

  const fromTakeOut = subcategory === 'taking_out_vloc';

  const subCategory = fromTakeOut ? 'taking_out_vloc' : 'vloc_application';

  useBackHandler(fromTakeOut ? goBack : goHome);

  const goToMoneyInTheBank = (param: DisburseFromLineOfCreditSuccess) => {
    router.push({
      pathname: '/varo-line-of-credit/money-in-the-bank',
      params: {
        moneyInTheBankTitle: param.title,
        moneyInTheBankMessage: param.message,
        takeOutSubCategory: subCategory,
      },
    });
  };

  const goToEntryPoint = () => {
    router.push({
      pathname: '/varo-line-of-credit/line-of-credit-entry-point',
    });
  };

  useEffect(() => {
    if (
      reviewData?.disburseFromLineOfCredit.__typename ===
      Typenames.DisburseFromLineOfCreditSuccess
    ) {
      if (
        reviewData.disburseFromLineOfCredit?.action ===
        PostDisbursementAction.InTheBank
      ) {
        const successResponse =
          (reviewData.disburseFromLineOfCredit as DisburseFromLineOfCreditSuccess) ??
          null;
        const param = {
          lineOfCreditId: successResponse?.lineOfCreditId,
          title: successResponse?.title,
          message: successResponse?.message,
          action: PostDisbursementAction.InTheBank,
        };
        goToMoneyInTheBank?.(param);
      } else if (
        reviewData.disburseFromLineOfCredit?.action ===
        PostDisbursementAction.WhereToGo
      ) {
        goToEntryPoint();
      }
    } else if (
      reviewData?.disburseFromLineOfCredit.__typename ===
      Typenames.DisburseFromLineOfCreditError
    ) {
      setShowError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewData]);

  const renderListItem = (label: string, value: string | undefined) => (
    <View>
      <ListItem
        primaryCopy={label}
        lineFormat={ListItemLineFormat.LABEL}
        trailingAccessory={{
          id: '1',
          name: VXLComponentId.LIST_ITEM_TEXT_STACK,
          props: { primaryCopy: value ?? '' },
        }}
        onPress={
          label === translatedText('review.listItems.term')
            ? () =>
                router.push({
                  pathname: '/varo-line-of-credit/payment-schedule',
                  params: {
                    paymentScheduleId: storePaymentSchedule(
                      reviewInfo?.vlocReviewDetailsV2?.paymentSchedule ?? [],
                    ),
                    totalAmount:
                      reviewInfo?.vlocReviewDetailsV2?.totalAmount
                        .formattedWithPlusPrefix,
                    // For user coming from review we can assume that the paid amount is $0.00
                    paidToDate: '$0.00',
                    subcategory: subCategory,
                  },
                })
            : undefined
        }
        showsChevron={label === translatedText('review.listItems.term')}
        analyticsId="payment-schedule"
        customAttributes={{
          subcategory,
        }}
      />
      {label !== translatedText('review.listItems.firstPaymentDue') ? (
        <View style={styles.dividerStyle}>
          <HairLineDivider />
        </View>
      ) : null}
    </View>
  );

  const openBottomSheet = (title: string, description: string) => {
    setContent({ title, description });
    bottomSheetRef.current?.present();
  };

  const closeBottomSheet = () => bottomSheetRef.current?.dismiss();

  const isError = showError || error || mutationError;
  const isLoading = fetching || isFetching;

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <ScrollView contentContainerStyle={styles.scrollViewContent}>
        <Stack.Screen
          options={{
            headerBackVisible: true,
            headerShown: !isError && !isFetching,
            gestureEnabled: fromTakeOut && !isError,
            header: (props: NativeStackHeaderProps) => (
              <ExpoNavigationBar
                primaryIcon={<Icon iconKey={fromTakeOut ? 'BACK' : 'CLOSE'} />}
                primaryOnPress={fromTakeOut ? goBack : goHome}
                {...props}
              />
            ),
          }}
        />
        <View style={styles.content}>
          {!reviewData ? (
            <View style={styles.content}>
              {reviewInfo?.title ? (
                <TitleLgText style={styles.titleStyle} numberOfLines={0}>
                  {reviewInfo.title}
                </TitleLgText>
              ) : null}
              {reviewInfo?.description ? (
                <BodyBaseText style={styles.enhancedStyle} numberOfLines={0}>
                  {reviewInfo.description}
                </BodyBaseText>
              ) : null}
              <BodySmText style={styles.linOfCreditAmtStyle} numberOfLines={0}>
                {translatedText('review.lineOfCreditAmount')}
              </BodySmText>
              {reviewInfo?.vlocReviewDetailsV2?.vlocAmount
                ?.formattedWithPlusPrefix ? (
                <ExpressiveLgText style={styles.vlocAmtStyle} numberOfLines={0}>
                  {
                    reviewInfo.vlocReviewDetailsV2.vlocAmount
                      .formattedWithPlusPrefix
                  }
                </ExpressiveLgText>
              ) : null}
              <HairLineDivider />
              {renderListItem(
                translatedText('review.listItems.fee'),
                reviewInfo?.vlocReviewDetailsV2?.fee?.formattedWithPlusPrefix,
              )}
              {renderListItem(
                translatedText('review.listItems.total'),
                reviewInfo?.vlocReviewDetailsV2?.totalAmount
                  .formattedWithPlusPrefix,
              )}
              {renderListItem(
                translatedText('review.listItems.term'),
                reviewInfo?.vlocReviewDetailsV2?.terms,
              )}
              {renderListItem(
                translatedText('review.listItems.paymentAmount'),
                reviewInfo?.vlocReviewDetailsV2?.paymentAmount
                  .formattedWithPlusPrefix,
              )}
              {renderListItem(
                translatedText('review.listItems.firstPaymentDue'),
                reviewInfo?.vlocReviewDetailsV2?.firstPaymentDue,
              )}
              {!reviewInfo?.isAutoPayEnabled ? (
                <View>
                  {reviewInfo?.autopayDisclaimer ? (
                    <BodyBaseText style={styles.commonStyle} numberOfLines={0}>
                      {reviewInfo.autopayDisclaimer}
                    </BodyBaseText>
                  ) : null}
                  <ListItem
                    primaryCopy={reviewInfo?.autopayTitle ?? ''}
                    lineFormat={ListItemLineFormat.LABEL}
                    trailingAccessory={{
                      id: '1',
                      name: VXLComponentId.SWITCH,
                      props: {
                        isOn: autoPay,
                        showStatusLabel: true,
                        onValueChange: () => setAutoPay((prev) => !prev),
                        analyticsId: autoPay ? 'autopay-off' : 'autopay-on',
                        customAttributes: { subcategory: subcategory },
                      },
                    }}
                    accessibilityLabel="Autopay"
                    showsChevron={false}
                  />
                  <View style={styles.dividerStyle}>
                    <HairLineDivider />
                  </View>

                  <HyperlinkSmText
                    onPress={() => {
                      openBottomSheet(
                        reviewInfo?.autoPayBottomSheet?.title || '',
                        reviewInfo?.autoPayBottomSheet?.description || '',
                      );
                    }}
                    analyticsId="faq"
                    customAttributes={{
                      subcategory: subcategory,
                      faq_name: 'autopay',
                    }}
                    style={styles.enhancedStyle}
                    numberOfLines={0}
                  >
                    {translatedText('review.whatIsAutoPay')}
                  </HyperlinkSmText>
                  <BodyXSText style={styles.enhancedStyle} numberOfLines={0}>
                    {translatedText('review.preferNotToMakePayment')}
                    <HyperlinkXsText
                      onPress={() =>
                        openBottomSheet(
                          reviewInfo?.payByCheckBottomSheet?.title || '',
                          reviewInfo?.payByCheckBottomSheet?.description || '',
                        )
                      }
                      analyticsId="payByCheck"
                      customAttributes={{
                        subcategory: subcategory,
                      }}
                      numberOfLines={0}
                    >
                      {translatedText('review.payByCheck')}
                    </HyperlinkXsText>
                    {translatedText('review.instead')}
                  </BodyXSText>
                  {reviewInfo?.disclaimer ? (
                    <BodyXSText style={styles.enhancedStyle} numberOfLines={0}>
                      {reviewInfo.disclaimer}
                    </BodyXSText>
                  ) : null}
                </View>
              ) : null}

              <View style={styles.bottomContainer}>
                {reviewInfo?.isAutoPayEnabled ? <HairLineDivider /> : null}
                <View style={styles.buttonContainer}>
                  <Button
                    label={translatedText('review.buttons.getLineOfCredit')}
                    onPress={() => submitReviewDetails(tierId, autoPay)}
                    analyticsId="submitReviewDetails"
                    customAttributes={{
                      subcategory,
                    }}
                  />
                  {fromTakeOut ? (
                    <Button
                      label={translatedText('review.buttons.cancel')}
                      type="SECONDARY"
                      onPress={goHome}
                      analyticsId="flow_exited"
                      customAttributes={{
                        subcategory,
                      }}
                    />
                  ) : null}
                </View>
              </View>
            </View>
          ) : null}
        </View>
        <ScreenTransitionOverlay
          isLoading={isLoading}
          LoadingComponent={<FullScreenLoading isCoinLoader={isFetching} />}
          ErrorComponent={
            isError ? (
              <LineOfCreditErrorScreenComponent
                screenAnalyticsId={`/varo-line-of-credit/LineOfCreditErrorScreenComponent-reviewScreen/${subCategory}`}
                trackingAnalyticsId="payment-review-error-screen-tracking"
                trackingCustomAttributes={{
                  step_name: VlocStepName.VLOC_DISBURSEMENT_CONFIRMATION,
                }}
              />
            ) : null
          }
        />

        <BottomSheet ref={bottomSheetRef}>
          <View style={styles.bottomSheetContainer}>
            {content?.title ? (
              <TitleMdText numberOfLines={0}>{content.title}</TitleMdText>
            ) : null}
            {content?.description ? (
              <BodyBaseText style={styles.bottomSheetBody} numberOfLines={0}>
                {content.description}
              </BodyBaseText>
            ) : null}
            <View style={styles.bottomSheetButtonContainer}>
              <Button
                label={translatedText('review.buttons.gotIt')}
                type="PRIMARY"
                onPress={closeBottomSheet}
              />
            </View>
          </View>
        </BottomSheet>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  bottomContainer: {
    marginTop: 'auto',
  },
  bottomSheetBody: {
    marginVertical: theme.spacing.m,
  },
  bottomSheetButtonContainer: {
    paddingVertical: theme.spacing.m,
  },
  bottomSheetContainer: {
    paddingHorizontal: theme.spacing.m,
  },
  buttonContainer: {
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  commonStyle: {
    margin: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  content: {
    flex: 1,
  },
  dividerStyle: {
    marginHorizontal: theme.spacing.m,
  },
  enhancedStyle: {
    marginHorizontal: theme.spacing.m,
    marginTop: theme.spacing.m,
  },
  linOfCreditAmtStyle: {
    color: theme.color.text.secondary,
    marginHorizontal: theme.spacing.m,
    marginTop: theme.spacing.xl,
  },
  scrollViewContent: {
    flexGrow: 1,
  },
  titleStyle: {
    marginHorizontal: theme.spacing.m,
  },
  vlocAmtStyle: {
    marginBottom: theme.spacing.m,
    marginHorizontal: theme.spacing.m,
  },
});

export default ReviewScreen;
